// Verwaltung › Rollen & Rechte: Matrix Recht × Rolle mit Grenzen, Rolle anlegen/kopieren/umbenennen/löschen.
// Der Standortbezug steckt nicht in der Rolle, sondern in ihrer Zuweisung an eine Person (platform.user.assignRole).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

export const zLimitDef = z.object({
  key: z.string(),
  label: z.string(),
  /** bp: Basispunkte (100 = 1 %) · cents: Cent · qty: Tausendstel der Basiseinheit · count: Anzahl */
  unit: z.enum(['bp', 'cents', 'qty', 'count']),
})

export const zCatalogRight = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  module: z.string(),
  /** Gruppierung innerhalb des Moduls („Lager“, „Verwaltung“) */
  group: z.string(),
  locationScoped: z.boolean(),
  sensitive: z.boolean(),
  limits: z.array(zLimitDef),
})

export const zRoleCell = z.object({
  /** Recht ist gewährt – direkt oder über ein Muster */
  granted: z.boolean(),
  /** Muster, über das das Recht kommt ('*', 'sales.*'); null = eigene Zeile */
  via: z.string().nullable(),
  /** Grenzen: Schlüssel → Höchstwert; fehlt der Schlüssel oder ist er null, gilt keine Grenze */
  limits: z.record(z.string(), z.number().nullable()),
})

export const zRole = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** mitgelieferte Rolle: lässt sich nicht löschen */
  isSystem: z.boolean(),
  /** hält '*' – alle Rechte, auch künftige (Geschäftsführung): ihre Rechte lassen sich nicht einschränken */
  allRights: z.boolean(),
  /** Personen mit dieser Rolle (ohne technische Zugänge) */
  people: z.int().nonnegative(),
  /** davon Zuweisungen, die auf einen Standort beschränkt sind */
  scopedAssignments: z.int().nonnegative(),
  assignments: z.int().nonnegative(),
  /** Recht → Zelle (nur gewährte Rechte) */
  rights: z.record(z.string(), zRoleCell),
  /** darf die angemeldete Person diese Rolle vergeben (hält sie alles, was die Rolle gewährt)? */
  assignable: z.boolean(),
})

export const roleList = defineContract({
  name: 'platform.role.list',
  title: 'Rollen und Rechte anzeigen',
  description: 'Alle Rollen des Betriebs mit ihren Rechten und Grenzen – und der Katalog der Rechte je Modul.',
  llm: {
    description:
      'Liefert die Rollen des Betriebs (Name, Beschreibung, Zahl der Personen) mit ihren Rechten und Grenzen sowie den ' +
      'Katalog aller Rechte je Modul. Verwenden für „Welche Rolle darf Preise ändern?“ oder „Was darf die Filialleitung?“. ' +
      'Für eine einzelne Person platform.user.rights verwenden.',
    keywords: ['Rolle', 'Rechte', 'Berechtigung', 'Matrix', 'Grenze', 'wer darf'],
  },
  input: z.strictObject({}),
  output: z.object({
    roles: z.array(zRole),
    modules: z.array(z.object({ id: z.string(), title: z.string(), active: z.boolean() })),
    rights: z.array(zCatalogRight),
  }),
  permission: 'platform.role.read',
  risk: 'read',
})

const zRoleName = z.string().trim().min(2, { error: 'Bitte geben Sie der Rolle einen Namen.' }).max(80)
const zRoleDescription = z.string().trim().max(300)

export const roleCreate = defineContract({
  name: 'platform.role.create',
  title: 'Rolle anlegen',
  description: 'Legt eine neue Rolle an – leer oder als Kopie einer bestehenden Rolle mit deren Rechten und Grenzen.',
  llm: { description: 'Legt eine Rolle an. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    name: zRoleName,
    description: zRoleDescription.optional(),
    /** Rolle, deren Rechte und Grenzen übernommen werden */
    copyFromRoleId: zId.optional(),
  }),
  output: z.object({ id: zId, key: z.string(), copiedRights: z.int().nonnegative() }),
  permission: 'platform.role.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const roleUpdate = defineContract({
  name: 'platform.role.update',
  title: 'Rolle umbenennen',
  description: 'Ändert Name und Beschreibung einer Rolle. Die Rechte bleiben unverändert.',
  llm: { description: 'Ändert Name und Beschreibung einer Rolle. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ roleId: zId, name: zRoleName.optional(), description: zRoleDescription.nullable().optional() }),
  output: z.object({ id: zId }),
  permission: 'platform.role.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const roleRemove = defineContract({
  name: 'platform.role.remove',
  title: 'Rolle löschen',
  description: 'Löscht eine eigene Rolle samt ihren Zuweisungen. Mitgelieferte Rollen bleiben.',
  llm: { description: 'Löscht eine Rolle. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ roleId: zId }),
  output: z.object({ removed: z.boolean(), affectedPeople: z.int().nonnegative() }),
  permission: 'platform.role.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const zRightChange = z.strictObject({
  /** Rechteschlüssel aus dem Katalog (kein Muster) */
  permission: z.string().min(3).max(120),
  granted: z.boolean(),
  /**
   * Grenzen des Rechts: Schlüssel der Grenze → Höchstwert in ihrer Einheit (Cent, Basispunkte, Anzahl); null =
   * unbegrenzt. Ohne Angabe bleiben die bisherigen Grenzen stehen.
   */
  limits: z.record(z.string(), z.int({ error: 'Bitte eine ganze Zahl eingeben.' }).min(0, { error: 'Eine Grenze darf nicht negativ sein.' }).nullable()).optional(),
})

export const roleChange = defineContract({
  name: 'platform.role.change',
  title: 'Rechte einer Rolle ändern',
  description:
    'Gewährt oder entzieht einer Rolle Rechte und stellt Grenzen ein. Die Vorschau nennt, wer dadurch was gewinnt und verliert; gespeichert wird erst nach Bestätigung und frischer Anmeldung.',
  llm: { description: 'Ändert Rechte und Grenzen einer Rolle. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    roleId: zId,
    changes: z.array(zRightChange).min(1, { error: 'Es gibt nichts zu ändern.' }).max(400),
  }),
  output: z.object({
    roleId: zId,
    granted: z.array(z.string()),
    revoked: z.array(z.string()),
    limitsChanged: z.array(z.string()),
    affectedPeople: z.int().nonnegative(),
  }),
  permission: 'platform.role.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  limits: { maxItems: 400, itemCount: (input) => input.changes.length },
  assistant: 'never',
})
